import { RiPushpinFill } from "react-icons/ri"
import { FileInputExtended, TextLargeInputWithTags } from "./Input-Components"
import { useState, useEffect, useContext, useRef, useLayoutEffect } from "react";
import { AccountContext } from "../contexts/AccountContexts";
import { GenericMessage, KeyboardKeyIcon, MessageDeletedMessage } from "./MessageComponents";
import { onDeleteNote, onEditNote, onNewNote, sendNote, sendNoteWithFileLinks } from "../utils/messagingUtils";
import { requestNotes } from "../requests/notes_requests";
import { ContentContext } from "../contexts/ContentContext";
import { FiArrowDownCircle } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { devWarn } from "../utils/loggerUtils";
import { NotificationContext } from "../contexts/NotificationContexts";
import { requestFetchTicketsByTimestamp } from "../requests/ticket_requests";
import { MdClose } from "react-icons/md";


export const NoteViewer = ({ socket, notesId, ticketInfo, replyTo, setReplyTo }) => {
    const { t } = useTranslation('edit')

    const { user, devs } = useContext(AccountContext)
    const { openErrorModal, isDarkMode } = useContext(ContentContext)
    const { reconnected } = useContext(NotificationContext)

    const notesContainerRef = useRef(null);
    const [isAtBottom, setIsAtBottom] = useState(true)
    const [gotNewMessages, setGotNewMessages] = useState(false)
    const [notes, setNotes] = useState([]);

    const _setGotNewMessages = () => {
        setGotNewMessages(true)
    }

    useEffect(() => {
        if (socket || reconnected) {
            socket.emit('join_chatroom', { room_id: ticketInfo.notes_id, name: user.id })

            onNewNote({ socket: socket, setNotes: setNotes, setGotNewMessages: _setGotNewMessages })

            onDeleteNote({ socket: socket, setNotes: setNotes })

            onEditNote({ socket: socket, setNotes: setNotes })

        }
    }, [notesId, reconnected])

    useEffect(() => {
        return () => {
            if (socket) {

                devWarn("Socket disconnected. NOTES", notesId)
                socket.emit('leave_chatroom', { room_id: ticketInfo.notes_id, name: user.id });
            }
        }
    }, [])


    useLayoutEffect(() => {
        const notesContainer = notesContainerRef.current;
        if (notesContainer && isAtBottom) {
            // Use setTimeout to ensure it runs after rendering is complete
            setTimeout(() => {
                notesContainer.scrollTop = notesContainer.scrollHeight;
            }, 50);
        }
    }, [notes]);


    const checkIfAtBottom = () => {
        if (!notesContainerRef.current) return;
        const { scrollTop, scrollHeight, clientHeight } = notesContainerRef.current;
        const atBottom = scrollHeight - scrollTop <= clientHeight + 50; // Small buffer
        setIsAtBottom(atBottom);
        if (atBottom) {
            setGotNewMessages(false)
        }
    };

    // Scroll to the bottom of the chat window
    const scrollToBottom = () => {
        if (notesContainerRef.current) {
            notesContainerRef.current.scrollTo({
                top: notesContainerRef.current.scrollHeight,
                behavior: "smooth",
            });
        }
    };

    useEffect(() => {
        const chatWindow = notesContainerRef.current;
        if (!chatWindow) return;

        const handleScroll = () => {
            checkIfAtBottom();
        };

        chatWindow.addEventListener("scroll", handleScroll);
        return () => {
            chatWindow.removeEventListener("scroll", handleScroll);
        };
    }, []);


    useEffect(() => {
        if (notesId) {
            requestNotes({ notesId: notesId, ticketId: ticketInfo.id })
                .then((data) => {
                    setNotes(data.notes)
                })
                .catch((error) => {
                    console.error(error.status, error.message)
                    openErrorModal(error)
                })
        }
    }, [notesId])


    useEffect(() => {
        if (isAtBottom) {
            scrollToBottom();
        }
    }, [notes, isAtBottom]);


    const handleSendMessage = (newMessage, newFiles, taggedUsers, taggedTickets, setMessageSent) => {

        if (newFiles.length > 0) {
            sendNoteWithFileLinks({ socket: socket, message: newMessage, taggedTickets: taggedTickets, taggedUsers: taggedUsers, files: newFiles, ticketInfo: ticketInfo, user: user, replyTo: replyTo?.type === "note" ? replyTo : null })
                .then(() => {
                    setMessageSent(true)
                    replyTo?.type === "note" && setReplyTo(null)
                })
                .catch((error) => {
                    console.error(error.status, error.message)
                    openErrorModal(error)
                })
        }
        else {
            sendNote({ socket: socket, message: newMessage, taggedUsers: taggedUsers, taggedTickets: taggedTickets, ticketInfo: ticketInfo, user: user, replyTo: replyTo?.type === "note" ? replyTo : null })
                .then(() => {
                    setMessageSent(true)
                    replyTo?.type === "note" && setReplyTo(null)
                })
                .catch((error) => {
                    console.error(error.status, error.message)
                    openErrorModal(error)
                })
        }

    }


    const reloadNotes = () => {
    }

    return (
        <div className="flex flex-col lg:max-h-full min-w-[40%] w-full h-full lg:h-auto rounded-xl bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-300 items-center overflow-hidden">
            <div className="flex flex-col mt-4 shrink-0 px-2 w-full">
                <h1 className=" text-center text-lg font-semibold flex items-center justify-center gap-2 w-full"><RiPushpinFill /> {t('Notes')}</h1>
            </div>
            <div className="divider before:h-[1px] after:h-[1px] mb-0 h-[1px] shrink-0 w-full"></div>

            {/* VIEW PAST NOTES HERE */}
            <div className="relative w-full flex flex-col min-h-0 bg-yellow-100/20" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/bg-2000.png)`, backgroundRepeat: 'repeat' }}>
                <div className="flex flex-col w-full h-full min-h-[150px] lg:min-h-[500px] lg:max-h-[500px] rounded-t-lg overflow-hidden">
                    <div className={`flex flex-col w-full h-full overflow-y-auto overscroll-contain py-3 gap-2 ${isDarkMode && "bg-opacity-60 bg-black"}`} ref={notesContainerRef}>
                        {(!notes || notes.length === 0)
                            ?
                            <h1 className="w-full text-center ltr:italic text-sm">{t('No new notes')}</h1>
                            :
                            notes.map((note) =>
                                (note.type && note.type == "note") &&
                                <GenericMessage key={note.id} socket={socket} notesId={notesId} reloadChat={reloadNotes} attr={note} userAttr={devs[note.sender_id]} end={note.sender_id === user.id} isNotes={true} dev={true} replyTo={replyTo} setReplyTo={setReplyTo} />
                                ||
                                (note.type && note.type == "deleted_notification") &&
                                <MessageDeletedMessage key={note.id} attr={note} userAttr={devs[note.sender_id]} end={note.sender_id === user.id} dev={true} />
                            )}
                    </div>
                </div>
                {
                    !isAtBottom &&
                    <div className="absolute bottom-0 ltr:right-5 rtl:left-5 z-50">
                        <div className="relative">
                            {
                                gotNewMessages &&
                                <button onClick={scrollToBottom} className="absolute animate-ping text-amber-400 dark:text-amber-200 scale-50 opacity-75"><FiArrowDownCircle size={40} /></button>
                            }
                            <button onClick={scrollToBottom} className={gotNewMessages ? "text-amber-500 dark:text-amber-300" : "text-gray-800 dark:text-gray-300"}><FiArrowDownCircle size={40} /></button>
                        </div>
                    </div>
                }
            </div>
            <div className="flex-1 flex flex-col w-full relative z-10 border-t border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 rounded-b-xl">
                <NoteWriteComponent
                    handleSendMessage={handleSendMessage}
                    replyTo={replyTo}
                    setReplyTo={setReplyTo}
                />
            </div>
        </div>
    )
}


export const NoteWriteComponent = ({ handleSendMessage, replyTo, setReplyTo }) => {
    const { t } = useTranslation('edit')
    const [newMessage, setNewMessage] = useState("")
    const [newFiles, setNewFiles] = useState([])
    const { devs } = useContext(AccountContext)

    const [messageSent, setMessageSent] = useState(false);

    const [ctrlPressed, setCtrlPressed] = useState(false)

    const submitCondition = newMessage === "" && newFiles.length === 0

    const findTicket = ({ timestamp }) => {
        requestFetchTicketsByTimestamp({ timestamp: timestamp, verbose: false })
            .then(async (response) => {
                return await response?.data?.tickets
            })
    }

    const _setFiles = (f) => {
        f = f.filter((el) => {
            for (let i = 0; i < newFiles.length; i++) {
                if (newFiles[i].name === el.name && newFiles[i].lastModified === el.lastModified) {
                    return false
                }
            }
            return true
        })
        const _files = [...f, ...newFiles]
        setNewFiles(_files)
    }

    useEffect(() => {
        const handleKeyPress = (event) => {
            let element = document.getElementById("editableTagDiv")
            if (event.ctrlKey) {

                if (element !== document.activeElement) {
                    return null;
                }

                setCtrlPressed(true)

                if (event.key === 'Enter') {
                    onSendMessage();
                }
            }
        };

        const handleKeyUp = (event) => {
            if (event.key == "Control") {

                setCtrlPressed(false)
                if (ctrlPressed) {
                }
            }
        };
        document.addEventListener('keydown', handleKeyPress);
        document.addEventListener('keyup', handleKeyUp);

        return () => {
            document.removeEventListener('keydown', handleKeyPress);
            document.removeEventListener('keyup', handleKeyUp);
        };
    }, [newMessage, newFiles]);


    useEffect(() => {
        const handlePaste = (event) => {
            const activeElement = document.activeElement;
            if (!activeElement || activeElement.id !== "editableTagDiv") {
                return;
            }

            const items = event.clipboardData?.items
            if (!items) return

            const files = []

            for (let i = 0; i < items.length; i++) {
                const item = items[i]
                if (item.kind === "file") {
                    const blob = item.getAsFile()
                    if (blob) {
                        const file = new File([blob], `screenshot-${Date.now()}.png`, { type: blob.type })
                        files.push(file)
                    }
                }
            }

            if (files.length > 0) {
                setNewFiles(prev => [...prev, ...files])
                event.preventDefault()
            }
        }

        document.addEventListener("paste", handlePaste)

        return () => {
            document.removeEventListener("paste", handlePaste)
        }
    }, [])


    const parseTaggedUsers = (message) => {
        let taggedUsers = []
        const parts = message.split(/(@\[[^\]]+\]\w+|\@\w+|\@[a-zA-Z0-9_\-]+)/g);
        parts.map((part, index) => {
            if (part.startsWith('@')) {
                const username = part.slice(1);
                const taggedUser = Object.values(devs).find(user => user.name === username);
                if (taggedUser && !taggedUsers.includes(taggedUser.id)) {
                    taggedUsers.push(taggedUser.id)
                }
            }
        })
        return taggedUsers
    }

    const parseTaggedTicket = (message) => {
        let taggedTickets = []
        const parts = message.split(/(#\[[^\]]+\]\w+|\#\w+|\#[a-zA-Z0-9_\-]+)/g);

        parts.map(async (part) => {
            if (part.startsWith('#')) {
                const username = part.slice(1);
                taggedTickets = await findTicket({ timestamp: username })
            }
        })
        return taggedTickets
    }

    const onSendMessage = () => {
        if (!submitCondition) {
            let taggedUsers = parseTaggedUsers(newMessage)
            let taggedTickets = parseTaggedTicket(newMessage)

            handleSendMessage(newMessage, newFiles, taggedUsers, taggedTickets, setMessageSent)
            setNewMessage("")
            setNewFiles([]);
        }
    }

    useEffect(() => {
        if (replyTo?.type == "note") document.getElementById("notesEditableTagDiv")?.focus()
    }, [replyTo])

    return (
        <div className="flex flex-col w-full  px-4 shrink-0 gap-0 lg:gap-3 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-300 rounded-b-xl">
            <div className="flex flex-col w-full px-3 gap-1 flex-1 flex flex-col">
                <div className="px-3">{replyTo?.type === "note" && t("Replying to") + ":"} {replyTo?.type === "note" && (

                    <div className="bg-gray-200 dark:bg-gray-600 p-2 rounded-md mb-2 flex justify-between items-center relative">
                        <span><strong>{replyTo.sender_name}</strong>: {replyTo.message}</span>
                        <MdClose
                            className="font-bold text-2xl absolute -top-2 -right-1.5 bg-white rounded-full text-gray-500 cursor-pointer hover:scale-110 transition-all duration-200 ease-out"
                            onClick={() => setReplyTo(null)}
                        />
                    </div>
                )}

                </div>
                <div className={`label mt-5 px-3`}>
                    <span className="label-text text-base font-semibold text-gray-800 dark:text-gray-300">
                        {t('Message')}
                    </span>
                </div>
                <TextLargeInputWithTags placeHolder={"Ask your @collegues..."} classNames={"self-end h-32"} inputVariable={newMessage} inputSetVariable={setNewMessage} reset={messageSent} setReset={setMessageSent} />
            </div>
            <div className="flex gap-4 px-2 lg:my-5 items-center lg:min-h-[10px]">
                {newFiles.map((file, index) => {
                    const isImage = file.type.startsWith("image/")
                    const isVideo = file.type.startsWith("video/")
                    const fileUrl = URL.createObjectURL(file)

                    return (
                        <div key={index} className="flex flex-col items-center mt-2">
                            {isImage ? (
                                <div className="relative">
                                    <div
                                        onClick={() => setNewFiles(prev => prev.filter((f, i) => i !== index))}
                                        className="absolute -right-3 -top-3 cursor-pointer rounded-full p-0.5 bg-red-600 text-white  text-xl font-bold"
                                    >
                                        <MdClose />
                                    </div>
                                    <img
                                        src={fileUrl}
                                        alt={file.name}
                                        className="w-24 h-24 object-cover rounded border border-gray-500"
                                    />
                                </div>
                            ) : isVideo ?
                                (
                                    <div className="relative">
                                        <div
                                            onClick={() => setNewFiles(prev => prev.filter((_, i) => i !== index))}
                                            className="absolute -right-3 -top-3 cursor-pointer rounded-full p-0.5 bg-red-600 text-white text-xl font-bold"
                                        >
                                            <MdClose />
                                        </div>
                                        <video
                                            src={fileUrl}
                                            alt={file.name}
                                            autoPlay
                                            loop
                                            className="w-24 h-24 object-cover rounded border border-gray-500"
                                        />
                                    </div>
                                ) : (
                                    <div className="w-24 h-24 flex items-center justify-center bg-gray-200 text-xs rounded">
                                        {file.name.split(".").pop().toUpperCase()}
                                    </div>
                                )}
                            <p className="text-xs mt-1 truncate max-w-24">{file.name}</p>
                        </div>
                    )
                })}
            </div>
            <div className="flex flex-col w-full justify-between items-center gap-4 mt-auto">
                <div className="flex flex-col w-full lg:w-auto">
                    <p className="font-semibold px-1 text-sm mb-2 text-center lg:text-left">
                        {t('Attachments')} <small>({t('You can select multiple files')})</small>
                    </p>
                    <FileInputExtended inputVariable={newFiles} inputSetVariable={_setFiles} inputId={2} />
                </div>
                <button className={`group btn lg:btn-lg w-full mb-4 lg:w-32 border-none text-white font-semibold ${ctrlPressed ? "bg-fuchsia-700 hover:bg-fuchsia-600 text-yellow-50" : "bg-primary hover:bg-primary/80"}`}
                    disabled={submitCondition}
                    onClick={onSendMessage}
                >
                    <div className="flex items-center justify-center gap-1">
                        {
                            (!ctrlPressed || submitCondition) &&
                            <>
                                <kbd className="kbd kbd-sm bg-black/20 border-transparent text-white">Ctrl</kbd>
                                <span>+</span>
                            </>
                        }
                        <kbd className="kbd kbd-sm bg-black/20 border-transparent text-white">Enter</kbd>
                    </div>
                </button>
            </div>
        </div>
    )
}